/**
 * Gochara (Vedic transit) interpretation — shared by the Horoscope page and the
 * dashboard. Given live planetary transit signs (from `getPlanetTransits`) and a
 * zodiac sign treated as the Moon Rashi, it derives house positions and composes
 * a reading, lucky picks, and per-planet favourability.
 */

// Sidereal sign order (Aries … Pisces).
export const SIGN_NAMES = [
  'Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo',
  'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces',
]

// Rashi lord (Vedic ruling planet) per sign index.
const RASHI_LORD = ['Mars', 'Venus', 'Mercury', 'Moon', 'Sun', 'Mercury', 'Venus', 'Mars', 'Jupiter', 'Saturn', 'Saturn', 'Jupiter']

export const signIndex = (name) => SIGN_NAMES.indexOf(name)

// Houses (from the Moon sign) where each planet's transit is auspicious.
export const GOCHARA_GOOD = {
  Sun: [3, 6, 10, 11],
  Moon: [1, 3, 6, 7, 10, 11],
  Mars: [3, 6, 11],
  Mercury: [2, 4, 6, 8, 10, 11],
  Jupiter: [2, 5, 7, 9, 11],
  Venus: [1, 2, 3, 4, 5, 8, 9, 11, 12],
  Saturn: [3, 6, 11],
  Rahu: [3, 6, 11],
}

const HOUSE_THEME = [
  'self and vitality', 'wealth and family', 'courage and initiative', 'home and inner peace',
  'creativity and romance', 'health and rivals', 'partnerships', 'change and challenges',
  'fortune and dharma', 'career and status', 'gains and friendships', 'rest and expenses',
]

const PLANET_TONE = {
  Sun: { good: 'lifting your confidence and standing', bad: 'so guard your energy and pride' },
  Moon: { good: 'steadying your emotions and bringing calm', bad: 'so tend gently to your mood and rest' },
  Mars: { good: 'fuelling drive, courage and quick wins', bad: 'so channel restless energy with care' },
  Mercury: { good: 'sharpening your words and clear thinking', bad: 'so double-check details and messages' },
  Jupiter: { good: 'opening doors to fortune, wisdom and growth', bad: 'so avoid over-optimism and excess' },
  Venus: { good: 'blessing love, comfort and creativity', bad: 'so keep balance in love and spending' },
  Saturn: { good: 'rewarding discipline and steady effort', bad: 'so stay patient and honour your duties' },
  Rahu: { good: 'amplifying ambition and bold moves', bad: 'so beware shortcuts and confusion' },
}

// Lucky colour follows the sign's ruling planet.
const PLANET_COLOUR = {
  Sun: 'Saffron', Moon: 'Ivory White', Mars: 'Coral Red', Mercury: 'Emerald Green',
  Jupiter: 'Golden Yellow', Venus: 'Rose Pink', Saturn: 'Royal Blue',
}

const PERIOD_FOCUS = {
  yesterday: ['Moon', 'Jupiter', 'Saturn'],
  today: ['Moon', 'Jupiter', 'Saturn'],
  tomorrow: ['Moon', 'Mars', 'Jupiter'],
  weekly: ['Sun', 'Mars', 'Jupiter'],
  monthly: ['Sun', 'Jupiter', 'Saturn'],
}
const PERIOD_INTRO = {
  yesterday: 'Looking back on yesterday, ',
  today: 'Today, ',
  tomorrow: 'Tomorrow, ',
  weekly: 'This week, ',
  monthly: 'This month, ',
}

const MOOD_BY_TONE = {
  bright: ['Joyful', 'Confident', 'Inspired', 'Energised'],
  steady: ['Hopeful', 'Focused', 'Grateful', 'Serene'],
  tender: ['Reflective', 'Calm', 'Patient', 'Contemplative'],
}

export function ordinal(n) {
  const s = ['th', 'st', 'nd', 'rd']
  const v = n % 100
  return n + (s[(v - 20) % 10] || s[v] || s[0])
}

// Short graha labels for a North-Indian chart cell.
export const PLANET_ABBR = {
  Sun: 'Su', Moon: 'Mo', Mars: 'Ma', Mercury: 'Me', Jupiter: 'Ju',
  Venus: 'Ve', Saturn: 'Sa', Rahu: 'Ra', Ketu: 'Ke',
}

/**
 * Build the 12 fixed North-Indian house cells from a kundli's Lagna + planet
 * signs. House positions are fixed (House 1 = ascendant, top-centre); each cell
 * carries its Rashi (the sign occupying it) and the grahas placed in it.
 * Returns `[{ house, rashi, sign, planets:[{ name, abbr, sign, deg }] }]` or null.
 */
export function buildKundliChart(kundli) {
  if (!kundli?.lagna || !Array.isArray(kundli.planets)) return null
  const lagnaIdx = signIndex(kundli.lagna)
  if (lagnaIdx < 0) return null
  const houses = Array.from({ length: 12 }, (_, i) => {
    const rashiIdx = (lagnaIdx + i) % 12
    return { house: i + 1, rashi: rashiIdx + 1, sign: SIGN_NAMES[rashiIdx], planets: [] }
  })
  kundli.planets.forEach((p) => {
    const h = houseFrom(p.sign, kundli.lagna)
    if (h) houses[h - 1].planets.push({ name: p.name, abbr: PLANET_ABBR[p.name] || p.name.slice(0, 2), sign: p.sign, deg: p.deg })
  })
  return houses
}

// House a transiting planet occupies counted from the Rashi (1–12).
export function houseFrom(planetSign, rashiSign) {
  const p = signIndex(planetSign)
  const r = signIndex(rashiSign)
  if (p < 0 || r < 0) return null
  return ((p - r + 12) % 12) + 1
}

// Per-planet transit status for a Rashi — used by the dashboard's influences panel.
export function planetTransitStatus(planet, planetSign, rashiSign) {
  const house = houseFrom(planetSign, rashiSign)
  const favourable = house != null && (GOCHARA_GOOD[planet] || []).includes(house)
  return {
    house,
    favourable,
    label: favourable ? 'Positive' : 'Neutral',
    note: house != null ? `In your ${ordinal(house)} house of ${HOUSE_THEME[house - 1]}` : '',
  }
}

/**
 * Compose a transit reading + lucky picks for a sign/period from live positions.
 * `transits` is `{ Sun: { sign, deg }, Moon: { sign, deg }, ... }`.
 */
export function buildTransitReading(signName, period, transits) {
  const idx = signIndex(signName)
  const houseOf = (planet) => (transits[planet] ? houseFrom(transits[planet].sign, signName) : null)
  const focus = PERIOD_FOCUS[period] || PERIOD_FOCUS.today
  let favCount = 0
  const clauses = focus
    .map((planet) => {
      const h = houseOf(planet)
      if (!h) return ''
      const good = (GOCHARA_GOOD[planet] || []).includes(h)
      if (good) favCount += 1
      const tone = PLANET_TONE[planet][good ? 'good' : 'bad']
      const verb = good ? 'moves auspiciously through' : 'passes through'
      return `${planet} ${verb} your ${ordinal(h)} house of ${HOUSE_THEME[h - 1]}, ${tone}`
    })
    .filter(Boolean)

  const closing =
    favCount >= 2 ? 'Overall the heavens favour you — act with quiet confidence.' :
    favCount === 1 ? 'A mixed yet promising phase — move steadily and stay open.' :
    'A time for patience and self-care — let matters ripen before you push.'

  const intro = PERIOD_INTRO[period] || 'Today, '
  const body =
    clauses.length === 0 ? 'the planets keep a calm, neutral watch over your sign.' :
    clauses.length === 1 ? `${clauses[0]}.` :
    `${clauses[0]}. Meanwhile, ${clauses[1]}${clauses[2] ? `, and ${clauses[2]}` : ''}.`
  const text = `${intro}${body} ${closing}`

  // Lucky picks derived from the live Moon transit + favourability.
  const moonDeg = Math.floor(transits.Moon?.deg || 0)
  const luckyNum = ((moonDeg + idx * 7 + favCount * 11) % 99) + 1
  const luckyColor = PLANET_COLOUR[RASHI_LORD[idx]] || 'Saffron'
  const moonHouse = houseOf('Moon')
  const moonGood = moonHouse != null && GOCHARA_GOOD.Moon.includes(moonHouse)
  const moodTone = favCount >= 2 && moonGood ? 'bright' : favCount >= 1 || moonGood ? 'steady' : 'tender'
  const moodBank = MOOD_BY_TONE[moodTone]
  const luckyMood = moodBank[moonDeg % moodBank.length]

  // Energy % (for the dashboard sign card) — favourability of all eight movers.
  const allGood = Object.keys(GOCHARA_GOOD).reduce((acc, pl) => {
    const h = houseOf(pl)
    return acc + (h && GOCHARA_GOOD[pl].includes(h) ? 1 : 0)
  }, 0)
  const energy = Math.round(45 + (allGood / 8) * 50) // 45–95%

  return { text, luckyNum, luckyColor, luckyMood, favCount, energy }
}
